import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../config/api';

const customStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=DM+Sans:wght@400;500&display=swap');
`;

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = customStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleSubscribe = async (plan) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(apiUrl('/api/stripe/create-checkout'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      });
      
      const data = await res.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to start checkout');
      }
    } catch (e) {
      alert('Network error');
    }
    setLoading(false);
  };

  const features = [
    'Multi-AI collaboration (Claude, GPT-4o, Grok, DeepSeek)',
    'Unlimited conversations',
    'File uploads & image analysis',
    'Real-time streaming responses',
    'Autonomous mode (5 rounds)',
    'User redirects mid-conversation',
    'Conversation summaries',
    'Transcript downloads',
    'Priority support'
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1c 0%, #0d1525 50%, #0a0f1c 100%)',
      fontFamily: "'DM Sans', sans-serif",
      color: 'white'
    }}>
      {/* Navigation */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px'
          }}>
            ⚡
          </div>
          <span style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '22px',
            fontWeight: '700',
            color: 'white'
          }}>
            BrainForge
          </span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Link to="/contact" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '15px' }}>
            Contact
          </Link>
          {user ? (
            <Link to="/dashboard" style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              borderRadius: '10px',
              color: 'white',
              textDecoration: 'none',
              fontSize: '15px',
              fontWeight: '600'
            }}>
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '15px' }}>
                Sign In
              </Link>
              <Link to="/signup" style={{
                padding: '10px 24px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '10px',
                color: 'white',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '600'
              }}>
                Get Started
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Header */}
      <section style={{
        padding: '60px 40px 40px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 'clamp(36px, 5vw, 52px)',
          fontWeight: '800',
          marginBottom: '16px'
        }}>
          Simple, Transparent Pricing
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.6)',
          fontSize: '18px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          Start with a 7-day free trial. No credit card required.
        </p>
      </section>

      {/* BYOK Notice */}
      <section style={{
        padding: '0 40px 40px',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1))',
          border: '2px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔑</div>
          <h3 style={{ color: '#fbbf24', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Bring Your Own Keys (BYOK)
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
            You provide your own API keys. AI costs are separate and paid directly to providers.
            <br />Typical conversation cost: <strong>$0.01 - $0.10</strong>
          </p>
        </div>
      </section>

      {/* Billing Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '40px'
      }}>
        <div style={{
          display: 'flex',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '12px',
          padding: '4px'
        }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '12px 28px',
              background: billingCycle === 'monthly' ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            style={{
              padding: '12px 28px',
              background: billingCycle === 'annual' ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            Annual
            <span style={{
              background: '#22c55e',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              Save 50%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Card */}
      <section style={{
        padding: '0 40px 80px',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '2px solid rgba(102, 126, 234, 0.4)',
          borderRadius: '24px',
          padding: '40px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Popular badge */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            ✨ Most Popular
          </div>

          <h2 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '28px',
            fontWeight: '700',
            marginBottom: '8px'
          }}>
            Pro Plan
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>
            Full access to all features
          </p>

          <div style={{ marginBottom: '32px' }}>
            <span style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '56px',
              fontWeight: '800'
            }}>
              ${billingCycle === 'annual' ? '59.99' : '9.99'}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '18px' }}>
              /{billingCycle === 'annual' ? 'year' : 'month'}
            </span>
          </div>

          {billingCycle === 'annual' && (
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '24px',
              color: '#22c55e',
              fontSize: '14px'
            }}>
              💰 Save $59.89/year compared to monthly
            </div>
          )}

          <button
            onClick={() => handleSubscribe(billingCycle)}
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none',
              borderRadius: '14px',
              color: 'white',
              fontSize: '17px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '32px',
              boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)'
            }}
          >
            {loading ? 'Loading...' : 'Start 7-Day Free Trial'}
          </button>

          <ul style={{
            textAlign: 'left',
            listStyle: 'none',
            padding: 0,
            margin: 0
          }}>
            {features.map((feature, i) => (
              <li key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 0',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '14px',
                borderBottom: i < features.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none'
              }}>
                <span style={{ color: '#22c55e' }}>✓</span>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{
        padding: '60px 40px',
        maxWidth: '800px',
        margin: '0 auto',
        borderTop: '1px solid rgba(255,255,255,0.06)'
      }}>
        <h2 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: '32px',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          Frequently Asked Questions
        </h2>
        
        {[
          {
            q: 'What are API keys and how do I get them?',
            a: 'API keys are credentials you get from AI providers (Anthropic, OpenAI, xAI, DeepSeek). We link to each provider in your settings. Most offer free credits to start.'
          },
          {
            q: 'How much do the AI models cost?',
            a: 'Costs vary by model and usage. A typical conversation costs $0.01-$0.10. Claude and GPT-4o are most expensive; DeepSeek is very affordable.'
          },
          {
            q: 'Can I cancel anytime?',
            a: 'Yes! Cancel with one click from your settings. You keep access until the end of your billing period.'
          },
          {
            q: 'Is my data secure?',
            a: 'Your API keys are encrypted with AES-256. Conversations are private to your account. We never share your data.'
          }
        ].map((faq, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px'
          }}>
            <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              {faq.q}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: '1.6' }}>
              {faq.a}
            </p>
          </div>
        ))}
      </section>

      {/* Footer */}
      <footer style={{
        padding: '40px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
            © 2025 BrainForge. All rights reserved.
          </div>
          <div style={{ display: 'flex', gap: '24px' }}>
            {['Terms', 'Privacy', 'Refund Policy', 'Contact'].map(item => (
              <Link
                key={item}
                to={`/${item.toLowerCase().replace(' ', '-')}`}
                style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '14px' }}
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
